import axios from '../axios';

// ---------------------------------------------------------------------------
// Estoque / Transferência entre PDVs — Service Layer (handoff 0099, Fase 1)
//
// Camada única da fronteira (FC1 §A.7): desembrulha o envelope JSend no sucesso
// (`data.data`) e propaga o `ApiError` que o interceptor de contrato já
// normalizou — nenhum parsing de erro acontece aqui. A tradução camelCase
// (args do front) → snake_case (payload/query do backend) mora SÓ neste
// arquivo.
//
// Fluxo PUSH em dois passos: ordem (pedido, reserva na origem) → remessa
// (expedição, sai da origem e fica em trânsito) → conferência no destino
// (entra pelo conferido) → tratativa da divergência (sem efeito de estoque).
// ---------------------------------------------------------------------------

// Toda MUTAÇÃO deste módulo sai com `_skipForbiddenRedirect` (2026-08-22).
//
// O escopo de PDV do backend responde 403 `pdv_access_denied` quando a ação
// toca um PDV fora do alcance do usuário — expedir/editar/cancelar exigem a
// origem, conferir exige o destino. Sem esta flag o interceptor global trataria
// isso como "página proibida" e faria `window.location.href = '/403'`: reload
// completo, levando junto a conferência já preenchida. Com a flag, o erro chega
// como `ApiError` no catch da tela e vira toast.
//
// As LEITURAS ficam de fora de propósito: um 403 num GET de detalhe significa
// mesmo que aquele registro não é do usuário, e aí o redirect é a resposta
// certa.
const ACTION_CONFIG = { _skipForbiddenRedirect: true };

// Meta de paginação do JSend — mesma leitura defensiva de `customersApi`.
function readPagination(envelope, fallbackPerPage = 20) {
   const meta = envelope?.meta ?? {};
   return {
      total: meta.total ?? 0,
      page: meta.page ?? 1,
      perPage: meta.per_page ?? fallbackPerPage,
      totalPages: meta.total_pages ?? 1,
   };
}

// Itens carregam campos de UI (prod_name, sku, saldos derivados) que não
// pertencem ao payload validado por Zod. A limpeza é da margem (aqui), não das
// telas — mesmo princípio de `toContractItems` em `movementsApi`.
function toOrderItems(items) {
   return (items ?? []).map((item) => ({
      prod_id: Number(item.prodId ?? item.prod_id),
      requested_qtd: Number(item.requestedQtd ?? item.requested_qtd),
   }));
}

function toShipmentItems(items) {
   return (items ?? []).map((item) => ({
      prod_id: Number(item.prodId ?? item.prod_id),
      shipped_qtd: Number(item.shippedQtd ?? item.shipped_qtd),
   }));
}

function toReceiveItems(items) {
   return (items ?? []).map((item) => {
      const payload = {
         prod_id: Number(item.prodId ?? item.prod_id),
         received_qtd: Number(item.receivedQtd ?? item.received_qtd),
      };
      // Só acompanha o item quando há falta — o backend exige o motivo apenas
      // em `received < shipped` e rejeita ruído no shape.
      const reason = (item.divergenceReason ?? '').trim();
      if (reason) payload.divergence_reason = reason;
      return payload;
   });
}

// Warnings da expedição (§3.6) — os dois arrays são NÃO-bloqueantes: a
// expedição já aconteceu quando eles chegam.
function readDispatchWarnings(data) {
   return {
      stockWarnings: data.stock_warnings ?? [],
      transferStockWarnings: data.transfer_stock_warnings ?? [],
   };
}

// ===========================================================================
// ORDENS DE TRANSFERÊNCIA
// ===========================================================================

// `kind` existe no contrato (PULL-ready) mas a Fase 1 é PUSH-only — o
// parâmetro fica disponível no service e não é exposto em tela.
//
// `orderBy` ∈ { id, created_at, updated_at, status, closed_at } (adendo §4.3);
// fora da whitelist o backend responde 422, não ignora. Omitir os dois = default
// do backend (`id desc`).
export async function listTransferOrders(filters = {}) {
   const { page, perPage, status, kind, pdvId, orderBy, orderDir } = filters;

   const params = {};
   if (page) params.page = page;
   if (perPage) params.per_page = perPage;
   if (status) params.status = status;
   if (kind) params.kind = kind;
   if (pdvId) params.pdv_id = pdvId;
   if (orderBy) params.order_by = orderBy;
   if (orderDir) params.order_dir = orderDir;

   const response = await axios.get('/transfer-orders', { params });
   const envelope = response?.data ?? {};

   return {
      orders: envelope.data?.transfer_orders ?? [],
      ...readPagination(envelope, perPage),
   };
}

// Detalhe: único ponto que traz `items[]` + `shipments[]` +
// `has_pending_divergence` computado (na listagem ele vem `null`).
export async function getTransferOrder(orderId) {
   const response = await axios.get(`/transfer-orders/${orderId}`);
   return response?.data?.data?.transfer_order ?? null;
}

export async function createTransferOrder({
   originPdvId,
   destPdvId,
   notes,
   items,
}) {
   const payload = {
      origin_pdv_id: Number(originPdvId),
      dest_pdv_id: Number(destPdvId),
      items: toOrderItems(items),
   };
   if (notes) payload.notes = notes;

   const response = await axios.post('/transfer-orders', payload, ACTION_CONFIG);
   return {
      order: response?.data?.data?.transfer_order ?? null,
      message: response?.data?.message ?? 'Transferência criada com sucesso.',
   };
}

// PUT parcial no header, mas `items` é o ESTADO COMPLETO desejado (mesmo
// padrão do editor de Movements): item ausente = remoção. Reduzir o pedido
// pode fechar a ordem — daí `status_transition` na resposta.
//
// `version` é OBRIGATÓRIO desde o handoff 0107 (2026-09-10, versão otimista):
// eco do `transfer_order.version` do GET que abriu a tela (ou da última
// mutação). Divergiu → 409 `transfer_version_conflict`. Regra de ouro: a tela
// adota a versão de TODA resposta de mutação — nunca faz `version + 1`.
export async function updateTransferOrder(
   orderId,
   { version, notes, destPdvId, items }
) {
   const payload = {};
   // Omitido só se a ordem carregada nem tinha o campo (backend pré-0107) —
   // ecoar um valor inventado geraria conflito falso.
   if (version !== undefined && version !== null) {
      payload.version = Number(version);
   }
   // Enviado sempre que a tela edita o campo (null limpa) — omitir deixaria a
   // observação antiga ao esvaziar o campo.
   if (notes !== undefined) payload.notes = notes || null;
   if (destPdvId !== undefined && destPdvId !== '') {
      payload.dest_pdv_id = Number(destPdvId);
   }
   if (items !== undefined) payload.items = toOrderItems(items);

   const response = await axios.put(
      `/transfer-orders/${orderId}`,
      payload,
      ACTION_CONFIG
   );
   const data = response?.data?.data ?? {};
   return {
      order: data.transfer_order ?? null,
      statusTransition: data.status_transition ?? null,
      message: response?.data?.message ?? 'Transferência atualizada.',
   };
}

export async function cancelTransferOrder(orderId, { cancelledReason }) {
   const response = await axios.post(
      `/transfer-orders/${orderId}/cancel`,
      { cancelled_reason: cancelledReason },
      ACTION_CONFIG
   );
   const data = response?.data?.data ?? {};
   return {
      order: data.transfer_order ?? null,
      message: response?.data?.message ?? 'Transferência cancelada.',
   };
}

// Posição derivada por (PDV, produto): físico / reservado / disponível /
// trânsito. Sem paginação no contrato — devolve a lista inteira do escopo.
export async function getStockPosition({ pdvId, prodId } = {}) {
   const params = {};
   if (pdvId) params.pdv_id = pdvId;
   if (prodId) params.prod_id = prodId;

   const response = await axios.get('/transfer-orders/stock-position', {
      params,
   });
   return response?.data?.data?.positions ?? [];
}

// ===========================================================================
// REMESSAS
// ===========================================================================

// EXPEDIÇÃO — cria a remessa e o movement de saída na origem.
//
// Quem transporta é um EMPLOYEE, não um user, e o campo é OPCIONAL desde o
// handoff 0108 (2026-09-11): nem todo funcionário tem login, e a remessa pode
// sair sem responsável designado. O contrato aceita o campo omitido, `null` ou
// um id de funcionário — omitimos quando não há seleção.
export async function dispatchShipment(
   orderId,
   { responsibleEmployeeId, notes, items }
) {
   const payload = {
      items: toShipmentItems(items),
   };
   if (responsibleEmployeeId) {
      payload.responsible_employee_id = Number(responsibleEmployeeId);
   }
   if (notes) payload.notes = notes;

   const response = await axios.post(
      `/transfer-orders/${orderId}/shipments`,
      payload,
      ACTION_CONFIG
   );
   const data = response?.data?.data ?? {};
   return {
      order: data.transfer_order ?? null,
      shipment: data.shipment ?? null,
      ...readDispatchWarnings(data),
      message: response?.data?.message ?? 'Remessa expedida com sucesso.',
   };
}

// `status` é SEMPRE enviado. Omitir significa `DISPATCHED` no backend, não
// "todos" — para todos existe o valor explícito `ALL` (adendo §4.3). Mandar
// sempre o param mantém o pedido igual ao que a tela promete.
//
// `orderBy` ∈ { id, dispatched_at, received_at, status }; default `id desc`.
export async function listTransferShipments(filters = {}) {
   const { page, perPage, status, pdvId, orderBy, orderDir } = filters;

   const params = {};
   if (page) params.page = page;
   if (perPage) params.per_page = perPage;
   if (status) params.status = status;
   if (pdvId) params.pdv_id = pdvId;
   if (orderBy) params.order_by = orderBy;
   if (orderDir) params.order_dir = orderDir;

   const response = await axios.get('/transfer-shipments', { params });
   const envelope = response?.data ?? {};

   return {
      shipments: envelope.data?.shipments ?? [],
      ...readPagination(envelope, perPage),
   };
}

// CONFERÊNCIA — precisa cobrir TODOS os itens da remessa; `received_qtd` nunca
// acima do expedido; motivo obrigatório na falta. Entra estoque pelo conferido.
export async function receiveShipment(shipmentId, { items }) {
   const response = await axios.post(
      `/transfer-shipments/${shipmentId}/receive`,
      { items: toReceiveItems(items) },
      ACTION_CONFIG
   );
   const data = response?.data?.data ?? {};
   return {
      order: data.transfer_order ?? null,
      shipment: data.shipment ?? null,
      stockWarnings: data.stock_warnings ?? [],
      statusTransition: data.status_transition ?? null,
      message: response?.data?.message ?? 'Remessa conferida com sucesso.',
   };
}

// Cancelamento em trânsito: devolve o estoque à origem (movement de reversão).
export async function cancelShipment(shipmentId, { cancelledReason }) {
   const response = await axios.post(
      `/transfer-shipments/${shipmentId}/cancel`,
      { cancelled_reason: cancelledReason },
      ACTION_CONFIG
   );
   const data = response?.data?.data ?? {};
   return {
      order: data.transfer_order ?? null,
      shipment: data.shipment ?? null,
      message: response?.data?.message ?? 'Remessa cancelada.',
   };
}

// TRATATIVA da divergência: ato auditado SEM efeito de estoque — fecha a
// contagem e documenta o desfecho. Pode destravar o CLOSED da ordem.
export async function settleShipmentDivergence(shipmentId, { items }) {
   const payload = {
      items: (items ?? []).map((item) => ({
         prod_id: Number(item.prodId ?? item.prod_id),
         settlement: (item.settlement ?? '').trim(),
      })),
   };

   const response = await axios.post(
      `/transfer-shipments/${shipmentId}/settle-divergence`,
      payload,
      ACTION_CONFIG
   );
   const data = response?.data?.data ?? {};
   return {
      order: data.transfer_order ?? null,
      shipment: data.shipment ?? null,
      statusTransition: data.status_transition ?? null,
      message: response?.data?.message ?? 'Tratativa registrada.',
   };
}
